import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Button } from "../components/ui";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { ContextBar } from "../components/context-bar";
import {
  NO_DISCORD_CHANGES,
  HUMAN_APPROVAL_REQUIRED,
  OnboardingOpsView,
  OnboardingWizard,
  RoleMenuBuilder,
  type WizardActions,
} from "../components/onboarding";
import { useSession } from "../hooks/useSession";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  buildOnboardingContent,
  buildRoleMenuContent,
  type OnboardingConfigResponse,
  type OnboardingStatusView,
  type RoleMenuContent,
} from "../lib/api";

const RELEASE_REVIEW_HREF = "/app/release-review";
const PERMISSIONS_GUIDE = "https://discord.com/developers/docs/topics/permissions";
const ROLE_POSITION_GUIDE = "https://support.discord.com/hc/en-us/articles/214836687-Role-Management-101";

type Tab = "wizard" | "operations" | "role-menus";

const TABS: { id: Tab; label: string }[] = [
  { id: "wizard", label: "Setup wizard" },
  { id: "operations", label: "Operations" },
  { id: "role-menus", label: "Role-menu builder" },
];

function toMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Something went wrong. Please try again.";
}

/**
 * Phase 6A community-onboarding admin surface. Server-driven and honest: every
 * value comes from the control API, drafts and dry-runs never touch Discord, and
 * deployment is only ever a hand-off to the existing Phase 4 approval flow.
 */
export function CommunityOnboarding(): JSX.Element {
  const { state } = useSession();
  const ctx = useAppContext();
  const [tab, setTab] = useState<Tab>("wizard");

  if (state.kind === "loading") return <LoadingState label="Checking your session…" />;
  if (state.kind === "not-configured")
    return (
      <NotConfiguredState title="Control API URL not configured">
        Set <code>VITE_CONTROL_API_URL</code> to manage community onboarding.
      </NotConfiguredState>
    );
  if (state.kind === "unavailable") return <ApiUnavailableState />;
  if (state.kind === "anonymous")
    return (
      <>
        <PageHeader title="Community onboarding" />
        <EmptyState title="Please sign in to continue">
          <Link to="/login">
            <Button variant="primary">Sign in with Discord</Button>
          </Link>
        </EmptyState>
      </>
    );

  const ready = Boolean(ctx.tenantId && ctx.guildId);

  return (
    <>
      <PageHeader title="Community onboarding & verification">
        <p>
          Design your welcome, rules-acknowledgement and member-verification flow. {NO_DISCORD_CHANGES} {HUMAN_APPROVAL_REQUIRED}
        </p>
      </PageHeader>

      {!apiConfigured ? (
        <NotConfiguredState title="Control API URL not configured">
          Set <code>VITE_CONTROL_API_URL</code> to manage community onboarding.
        </NotConfiguredState>
      ) : (
        <>
          <ContextBar requireGuild />
          {!ready ? (
            <EmptyState title="Select a workspace and server">
              Choose a workspace and enter your ODIN server id above to configure onboarding.
            </EmptyState>
          ) : (
            <>
              <div className="row ob-tabs" role="tablist" aria-label="Onboarding sections" style={{ gap: "0.5rem", margin: "1rem 0" }}>
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    role="tab"
                    aria-selected={tab === t.id}
                    className={`btn ${tab === t.id ? "btn--primary" : "btn--secondary"}`}
                    onClick={() => setTab(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {tab === "wizard" ? <WizardTab tenantId={ctx.tenantId!} guildId={ctx.guildId!} /> : null}
              {tab === "operations" ? <OperationsTab tenantId={ctx.tenantId!} guildId={ctx.guildId!} /> : null}
              {tab === "role-menus" ? <RoleMenusTab tenantId={ctx.tenantId!} guildId={ctx.guildId!} /> : null}
            </>
          )}
        </>
      )}
    </>
  );
}

function WizardTab({ tenantId, guildId }: { tenantId: string; guildId: string }): JSX.Element {
  const [config, setConfig] = useState<OnboardingConfigResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api
      .onboarding.getConfig(tenantId, guildId)
      .then((r) => active && setConfig(r))
      .catch((e) => active && setError(toMessage(e)));
    return () => {
      active = false;
    };
  }, [tenantId, guildId]);

  const actions: WizardActions = {
    createDraftAndValidate: async (module) => {
      const version = await api.onboarding.createDraftVersion(tenantId, guildId, buildOnboardingContent(module));
      const validation = await api.onboarding.validateVersion(tenantId, guildId, version.id);
      return { version, validation };
    },
    runDryRun: (blueprintVersionId) => api.onboarding.dryRun(tenantId, guildId, blueprintVersionId),
  };

  return (
    <>
      {error ? <ApiUnavailableState detail={error} /> : null}
      <OnboardingWizard liveConfig={config} actions={actions} releaseReviewHref={RELEASE_REVIEW_HREF} />
    </>
  );
}

function OperationsTab({ tenantId, guildId }: { tenantId: string; guildId: string }): JSX.Element {
  const [status, setStatus] = useState<OnboardingStatusView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    api
      .onboarding.getStatus(tenantId, guildId)
      .then((r) => {
        setStatus(r);
        setError(null);
      })
      .catch((e) => setError(toMessage(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // load reads the same tenant/guild props it depends on
  }, [tenantId, guildId]);

  if (loading && !status) return <LoadingState label="Reading onboarding status…" />;
  if (error && !status) return <ApiUnavailableState detail={error} />;
  if (!status) return <EmptyState title="No status available">Onboarding has not been configured for this server yet.</EmptyState>;

  return (
    <OnboardingOpsView
      status={status}
      remediation={{
        onRefresh: load,
        releaseReviewHref: RELEASE_REVIEW_HREF,
        permissionsGuideHref: PERMISSIONS_GUIDE,
        rolePositionGuideHref: ROLE_POSITION_GUIDE,
      }}
    />
  );
}

function RoleMenusTab({ tenantId, guildId }: { tenantId: string; guildId: string }): JSX.Element {
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submitAndValidate = async (menu: RoleMenuContent) => {
    setError(null);
    setResult(null);
    try {
      await api.onboarding.createRoleMenu(tenantId, guildId, { menuKey: menu.id, content: buildRoleMenuContent(menu) });
      const validation = await api.onboarding.validateRoleMenu(tenantId, guildId, menu.id);
      setResult(
        validation.summary.blocked
          ? `Server validation found ${validation.summary.blockers} blocker(s): ${validation.findings.map((f) => f.message).join("; ")}`
          : `Server validation passed with ${validation.summary.warnings} warning(s). ${NO_DISCORD_CHANGES}`,
      );
    } catch (e) {
      setError(toMessage(e));
    }
  };

  return (
    <>
      {error ? <ApiUnavailableState detail={error} /> : null}
      {result ? (
        <div className="state" role="status" style={{ marginBottom: "1rem" }}>
          <p>{result}</p>
        </div>
      ) : null}
      <RoleMenuBuilder onValidateRemote={submitAndValidate} onGenerateDryRun={() => setResult(`Add this menu to a blueprint version, then generate a dry run from the Setup wizard. ${NO_DISCORD_CHANGES}`)} />
    </>
  );
}
